import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import {
  getDailyConsultationsReasons,
  getDailyPatients,
  getDailyStatus,
  getPatientsByWeekByEtablissement,
  getRankConsultationsCountByEtablissement,
  getWeeklyConsultations,
  getWeeklyConsultationsByType,
  getWeeklySpecialtyConsultations,
  selectConsultationStatistics,
  selectPatientStatistics,
} from '@/redux/reducers/statistic.reducer'
import { useGetEstablishmentAIUsageStatisticsQuery } from '@/core/services/statistics'
import dayjs from 'dayjs'
import { AccordionSection } from './useAccordionState'

export const useStatisticsData = (
  establishmentId: string,
  selectedDate: Date,
  selectedRange: [Date, Date],
  expandedSection: AccordionSection | false,
  loadingStates: {
    overviewLoaded: boolean
    dailyLoaded: boolean
    weeklyLoaded: boolean
    setOverviewLoaded: (loaded: boolean) => void
    setDailyLoaded: (loaded: boolean) => void
    setWeeklyLoaded: (loaded: boolean) => void
  }
) => {
  const dispatch = useAppDispatch()

  const consultationStatistics = useAppSelector(selectConsultationStatistics)
  const patientStatistics = useAppSelector(selectPatientStatistics)

  const { data: aiUsageData, isLoading: isLoadingAIUsage } = useGetEstablishmentAIUsageStatisticsQuery(
    {
      establishment: establishmentId,
      startDate: dayjs(selectedRange[0]).format('YYYY-MM-DD'),
      endDate: dayjs(selectedRange[1]).format('YYYY-MM-DD'),
    },
    {
      skip: !establishmentId || expandedSection !== 'aiUsage',
      refetchOnMountOrArgChange: true,
    }
  )

  useEffect(() => {
    if (establishmentId === '' || expandedSection !== 'overview') return

    if (!loadingStates.overviewLoaded) {
      dispatch(getPatientsByWeekByEtablissement({ establishmentId }))
      dispatch(getRankConsultationsCountByEtablissement({ establishmentId }))
      loadingStates.setOverviewLoaded(true)
    }
  }, [dispatch, establishmentId, expandedSection, loadingStates])

  useEffect(() => {
    if (establishmentId === '' || expandedSection !== 'daily') return

    if (!loadingStates.dailyLoaded) {
      dispatch(getDailyStatus({ establishmentId, date: selectedDate.toISOString() }))
      dispatch(getDailyPatients({ establishmentId, date: selectedDate.toISOString() }))
      dispatch(getDailyConsultationsReasons({ establishmentId, date: selectedDate.toISOString() }))
      loadingStates.setDailyLoaded(true)
    }
  }, [dispatch, establishmentId, expandedSection, selectedDate, loadingStates])

  useEffect(() => {
    if (establishmentId === '' || expandedSection !== 'weekly') return

    if (!loadingStates.weeklyLoaded) {
      dispatch(
        getWeeklyConsultations({
          establishmentId,
          startDate: selectedRange[0].toISOString(),
          endDate: selectedRange[1].toISOString(),
        })
      )
      dispatch(
        getWeeklyConsultationsByType({
          establishmentId,
          startDate: selectedRange[0].toISOString(),
          endDate: selectedRange[1].toISOString(),
        })
      )
      dispatch(
        getWeeklySpecialtyConsultations({
          establishmentId,
          startDate: selectedRange[0].toISOString(),
          endDate: selectedRange[1].toISOString(),
        })
      )
      loadingStates.setWeeklyLoaded(true)
    }
  }, [dispatch, establishmentId, expandedSection, selectedRange, loadingStates])

  return {
    consultationStatistics,
    patientStatistics,

    aiUsageData,
    isLoadingAIUsage,
  }
}
